import { useCallback, useEffect, useId, useRef, type ReactNode } from 'react'
import { cn } from '../lib/cn'

/**
 * Диалог. Radix в проект не заводили, поэтому всё, что он дал бы, приходится делать
 * руками — и делать полностью, иначе клавиатурный пользователь остаётся в ловушке:
 *
 *   Escape закрывает;
 *   фокус уезжает внутрь при открытии и возвращается на место при закрытии;
 *   Tab и Shift+Tab ходят по кругу внутри окна и не выпадают на страницу за подложкой;
 *   заголовок и пояснение связаны с окном через aria-labelledby / aria-describedby,
 *     а не пересказаны в aria-label — иначе скринридер читает заголовок дважды.
 */

/** Что считается фокусируемым. Скрытые и отключённые элементы из обхода выпадают. */
const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'

export function Dialog({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  width = 'md',
}: {
  open: boolean
  onClose: () => void
  title: string
  description?: ReactNode
  children?: ReactNode
  footer?: ReactNode
  width?: 'sm' | 'md' | 'lg'
}) {
  const panelRef = useRef<HTMLDivElement>(null)
  const returnFocusTo = useRef<HTMLElement | null>(null)
  const titleId = useId()
  const descriptionId = useId()

  /**
   * onClose почти во всех местах передаётся стрелкой прямо в разметке, то есть на каждом
   * рендере это новая функция. Если эффект зависит от неё напрямую, он переподписывается
   * при любом обновлении диалога — а вместе с этим заново отбирает фокус и сохраняет
   * overflow у body. Пока данные встречи догружаются, фокус успевает прыгнуть несколько
   * раз. Поэтому обработчик живёт в ссылке, а эффект зависит только от open.
   */
  const closeRef = useRef(onClose)
  closeRef.current = onClose

  const focusables = useCallback((): HTMLElement[] => {
    const panel = panelRef.current
    if (!panel) return []
    return Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
      // Свёрнутые ветки диалога (радиокнопка «причина», пока не выбрана отмена) в обход
      // не попадают: Tab по невидимому элементу выглядит как потерянный фокус.
      (element) => element.getClientRects().length > 0 || element === document.activeElement,
    )
  }, [])

  useEffect(() => {
    if (!open) return
    returnFocusTo.current = document.activeElement as HTMLElement | null

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        closeRef.current()
        return
      }
      if (event.key !== 'Tab') return

      // Круговой обход. Без него Tab уходит на страницу под подложкой: визуально
      // человек в диалоге, а фокус — на кнопках, до которых мышью не дотянуться.
      const items = focusables()
      if (items.length === 0) {
        event.preventDefault()
        panelRef.current?.focus()
        return
      }
      const first = items[0]!
      const last = items[items.length - 1]!
      const active = document.activeElement as HTMLElement | null
      const inside = active ? panelRef.current?.contains(active) : false

      if (!inside) {
        event.preventDefault()
        ;(event.shiftKey ? last : first).focus()
        return
      }
      if (event.shiftKey && active === first) {
        event.preventDefault()
        last.focus()
        return
      }
      if (!event.shiftKey && active === last) {
        event.preventDefault()
        first.focus()
      }
    }

    // Всплытие, а не перехват: вложенная подсказка (список участников) должна успеть
    // забрать себе Escape и закрыться, не утащив за собой весь диалог.
    document.addEventListener('keydown', onKeyDown)

    // Фокус внутрь: на первый управляемый элемент, а если их нет — на само окно,
    // чтобы Escape и чтение с экрана работали сразу. Поле с autoFocus React успевает
    // сфокусировать раньше этого эффекта — тогда не перебиваем его выбор.
    const items = focusables()
    const active = document.activeElement as HTMLElement | null
    const alreadyInside = Boolean(active && panelRef.current?.contains(active) && active !== panelRef.current)
    if (!alreadyInside) (items[0] ?? panelRef.current)?.focus()

    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', onKeyDown)
      document.body.style.overflow = previousOverflow
      returnFocusTo.current?.focus?.()
    }
  }, [open, focusables])

  if (!open) return null

  const widths = { sm: 'max-w-sm', md: 'max-w-lg', lg: 'max-w-2xl' }

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-scrim/50 p-0 backdrop-blur-[2px] sm:items-center sm:p-4"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose()
      }}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={description ? descriptionId : undefined}
        tabIndex={-1}
        className={cn(
          'w-full rounded-t-card bg-surface p-5 shadow-pop outline-none sm:rounded-card',
          widths[width],
        )}
      >
        <h2 id={titleId} className="text-lg font-semibold">
          {title}
        </h2>
        {description && (
          <div id={descriptionId} className="mt-2 text-sm text-muted">
            {description}
          </div>
        )}
        {children && <div className="mt-4">{children}</div>}
        {footer && <div className="mt-6 flex flex-wrap justify-end gap-2">{footer}</div>}
      </div>
    </div>
  )
}
